'use client';

import { useCallback, useEffect, useState } from 'react';

import useSWR from 'swr';

import { useRouter } from '@/i18n/routing';
import { useBuyCryptoWidgetContext } from '@/providers/BuyCryptoWidgetProvider';

import { Fiat, RatesMap, transformToRatesMap } from '@/app/[lang]/buy-crypto/components/ConverterWidget/data';

import { getRates } from './api';

export const DEFAULT_SELECTED_TOKEN = 'BTC';

export type UseConverterProps = {
  shouldNavigate?: boolean;
  initialSelectedToken?: string;
};

export const useConverter = ({
  shouldNavigate = false,
  initialSelectedToken = DEFAULT_SELECTED_TOKEN,
}: UseConverterProps = {}) => {
  const router = useRouter();
  const { amount, setAmount, isPayAmountChanging, setIsPayAmountChanging, selectedFiat, setSelectedFiat } =
    useBuyCryptoWidgetContext();
  const [selectedToken, setSelectedToken] = useState(initialSelectedToken?.toUpperCase());
  const [ratesMap, setRatesMap] = useState<RatesMap>({});
  const [fiatOptions, setFiatOptions] = useState<string[]>([]);
  const [tokenOptions, setTokenOptions] = useState<string[]>([]);

  const { data, isLoading, error } = useSWR('rates', getRates, { refreshInterval: 3000 });

  useEffect(() => {
    if (data && data.length > 0) {
      const ratesMap = transformToRatesMap(data);
      setRatesMap(ratesMap);
      setTokenOptions(Object.keys(ratesMap));
      setFiatOptions((_) => {
        const newOptions = Object.keys(ratesMap[selectedToken].rates);
        if (newOptions?.length > 0 && !newOptions.includes(selectedFiat)) {
          setSelectedFiat(newOptions[0] as Fiat);
        }
        return newOptions;
      });
    }
  }, [data, selectedFiat, selectedToken, setSelectedFiat]);

  const currencyRate = ratesMap[selectedToken]?.rates[selectedFiat];
  let payAmount: string;
  let getAmount: string;

  if (isPayAmountChanging) {
    payAmount = amount;
    getAmount = amount ? (parseFloat(amount) / currencyRate).toFixed(8) : '';
  } else {
    payAmount = amount ? (parseFloat(amount) * currencyRate).toFixed(2) : '';
    getAmount = amount;
  }

  const handlePayAmountChange = useCallback((value: string) => {
    setIsPayAmountChanging(true);
    setAmount(value);
  }, []);

  const handleGetAmountChange = useCallback((value: string) => {
    setIsPayAmountChanging(false);
    setAmount(value);
  }, []);

  const handleFiatSelectChange = useCallback((value: string) => {
    setSelectedFiat(value as Fiat);
  }, []);

  const handleTokenSelectChange = useCallback((value: string) => {
    if (shouldNavigate) {
      router.push(`/buy-crypto/${value.toLowerCase()}`, { scroll: false });
    }
    setSelectedToken(value);
  }, []);

  return {
    isLoading,
    error,
    amount,
    payAmount,
    getAmount,
    fiatOptions,
    tokenOptions,
    selectedFiat,
    selectedToken,
    currencyRate,
    handlePayAmountChange,
    handleGetAmountChange,
    handleTokenSelectChange,
    handleFiatSelectChange,
  };
};
